/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useCallback, useMemo } from 'react';
import { css, jsx } from '@emotion/react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import Alert from '@mui/material/Alert';
import { useFileUploadActions } from '../../actions/FileActions';
import {
  useFolderActions,
  useFolderOrganizationQuery,
} from '../../actions/FolderActions';
import { useTranscriptUploadActions } from '../../actions/FileActions';

import { useUserQuery } from '../../actions/UserActions';
import { useRateLimitsQuery } from '../../actions/WorkspaceActions';

import { SUPPORTED_MIMETYPES } from '../../constants';
import { alertDialog } from '../../utils/dialog';
import { addErrorSnack } from '../../utils/snackbar';

function noop() {}

export default function UploadFileDropzone({
  children,
  folderPrefix,
  disabled,
  onUpload = noop,
  onUploadProgress = noop,
  onUploadDone = noop,
  noClick = false,
}) {
  const { t } = useTranslation();
  const folderId = folderPrefix
    .split('/')
    .filter((x) => x)
    .pop();

  const { user } = useUserQuery();
  const workspace = user?.workspace;
  const { uploadFileAsync, uploadError: uploadFileError } =
    useFileUploadActions();
  const { uploadTranscriptAsync, uploadError: uploadTranscriptError } =
    useTranscriptUploadActions();
  const { createFolderAsync } = useFolderActions(folderId);
  const { currentFolder } = useFolderOrganizationQuery({
    organizationId: workspace?.id,
    folderPrefix,
  });

  const { rateLimits } = useRateLimitsQuery({
    enabled: !!user?.role && user?.role !== 'guest' && !!user?.workspace,
  });

  const uploadsLeft = useMemo(() => {
    if (user?.workspace?.type === 'trial') {
      return rateLimits?.upload?.limit - rateLimits?.upload?.value;
    }
    return -1;
  }, [rateLimits, user?.workspace?.type]);

  const onDrop = useCallback(
    async (files) => {
      if (!files.length) return;

      const limit = user?.worksapce?.type === 'trial' ? uploadsLeft : 10000;
      if (files.length > limit) {
        files = files.slice(0, limit);
      }

      let dirs = new Set();
      files.forEach((file) => {
        if (file.path) {
          let split = file.path.split('/');
          for (let i = 0; i < split.length - 1; i++) {
            let dir = split.slice(0, i + 1).join('/');
            dirs.add(dir);
          }
        }
      });

      dirs = Array.from(dirs);
      // Sort dirs, making sure the parent dir is created first
      dirs.sort((a, b) => a.split('/').length - b.split('/').length);
      let dirsMap = {};

      if (dirs.length) {
        for (const dir of dirs) {
          let _folderPrefix = folderPrefix + '/';
          let split = dir.split('/').filter((x) => x);
          const name = split.pop();

          if (dirsMap[split.join('/')]) {
            _folderPrefix = dirsMap[split.join('/')];
          }
          // Remove last /
          // _folderPrefix = _folderPrefix.slice(0, -1);
          const parentId = _folderPrefix
            .split('/')
            .filter((x) => x)
            .pop();
          const res = await createFolderAsync({
            name,
            folder_prefix: _folderPrefix,
            parentId,
            organizationId: workspace.id,
            org_full_access: currentFolder?.org_full_access,
          });
          const id = res?.folder?.id;
          dirsMap[dir] = `${_folderPrefix}/${id}`.replace('//', '/');
        }
      }

      const filesProgress = files.reduce((acc, file) => {
        acc[file.name] = {
          name: file.name,
          progress: 0,
          uploading: true,
          mimetype: file.type,
        };
        return acc;
      }, {});

      const baseParams = {
        assignedTo: user.id,
        language: 'auto',
        numSpeakers: 0,
        outputFormat: 'transcript',
        transcribeMode: 0,
      };

      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        let _folderPrefix;
        if (file.path) {
          _folderPrefix = dirsMap[file.path.slice(0, -1)];
        } else {
          _folderPrefix = folderPrefix;
        }
        const params = {
          ...baseParams,
          folderPrefix: _folderPrefix,
          folder_prefix: _folderPrefix,
          name: file.name,
        };
        const mimeType = file.type;

        try {
          if (mimeType.startsWith('video') || mimeType.startsWith('audio')) {
            await uploadTranscriptAsync({
              form,
              ...params,
              onProgress: ({ progress }) => {
                filesProgress[file.name].progress = progress;
                onUploadProgress(filesProgress);
              },
            });
          } else {
            await uploadFileAsync({
              form,
              params,
              mimeType,
              onProgress: ({ progress }) => {
                filesProgress[file.name].progress = progress;
                onUploadProgress(filesProgress);
              },
            });
          }
        } catch (e) {
          if (e.response.code === 429) {
            alertDialog({
              title: t('För många filer'),
              content: t(
                'Du försöker ladda upp för många filer. Du kan ladda upp max {{uploadsLeft}} filer.',
                { uploadsLeft }
              ),
            });
          }
          filesProgress[file.name].error = e;
        }
      }
      onUploadDone();
    },
    [
      folderPrefix,
      onUploadProgress,
      onUploadDone,
      uploadFileAsync,
      createFolderAsync,
      user.id,
      workspace.id,
      currentFolder,
      uploadTranscriptAsync,
      uploadsLeft,
    ]
  );

  function addDirectory(item, processFile, path = '') {
    return new Promise((resolve, reject) => {
      if (item.isDirectory) {
        var directoryReader = item.createReader();
        directoryReader.readEntries(async function (entries) {
          let promises = entries.map((entry) =>
            addDirectory(entry, processFile, path + item.name + '/')
          );
          await Promise.all(promises);
          resolve();
        });
      } else {
        item.file(function (file) {
          processFile([file], 0, path);
          resolve();
        });
      }
    });
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    getFilesFromEvent: async (e) => {
      let list = [];
      function processFile(files, index, path = '') {
        // add to list
        if (typeof index !== 'undefined') {
          let file = files[index];
          file.path = path;
          list.push(file);
        } else {
          list = list.concat(files);
        }
      }

      // if directory support is available
      if (e.dataTransfer && e.dataTransfer.items) {
        var items = e.dataTransfer.items;
        let promises = [];

        for (var i = 0; i < items.length; i++) {
          var item = items[i].webkitGetAsEntry();

          if (item) {
            promises.push(addDirectory(item, processFile));
          }
        }

        await Promise.all(promises);

        return list;
      }

      // Fallback
      var files = e.target.files || e.dataTransfer.files;
      if (!files.length) {
        alert('File type not accepted');
        return;
      }
      return files;
    },
    onDrop,
    onDropRejected: (files) => {
      addErrorSnack(t('Ogiltig fil försökte laddas upp'));
    },
    noClick,
    accept: {
      'application/vnd.apple.pages': ['.pages'],
      'application/vnd.oasis.opendocument.text': ['.odt'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        ['.pptx'],
      'text/plain': ['.txt'],
      'application/rtf': ['.rtf'],

      'video/*': ['.mkv', '.mov'],
      'audio/*': ['.WMA', '.wma'],
      ...SUPPORTED_MIMETYPES.reduce((acc, type) => {
        acc[type] = [];
        return acc;
      }, {}),
    },
  });

  return (
    <>
      <div
        {...getRootProps()}
        css={css`
          cursor: ${!disabled && !noClick ? 'pointer' : 'default'};
          flex-grow: 1;

          display: flex;
          ${isDragActive ? 'background-color: var(--primary-8p);' : ''}
          ${isDragActive
            ? 'border: 2px solid var(--primary-main);'
            : 'border: 2px dashed white;'}
          border-radius: 8px;
        `}
      >
        <input {...getInputProps()} />
        <div
          css={css`
            opacity: ${isDragActive ? 0.5 : 1};
            width: 100%;
            height: 100%;
          `}
        >
          {uploadTranscriptError?.code === 'rate_limit' ? (
            <Alert
              severity="warning"
              css={css`
                border-radius: 0.5rem;
                background-color: var(--warning-4p);
              `}
            >
              {t('Du har en max gräns på {{limit}} filer per timme', {
                limit: uploadTranscriptError?.limit,
              })}
            </Alert>
          ) : null}
          {children}
        </div>
      </div>
    </>
  );
}
